<template>
  <Teleport to="body">
    <Transition name="transaction-editor">
      <div v-if="visible" class="transaction-editor-layer" @click.self="close">
        <section
          class="transaction-editor"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transaction-editor-title"
        >
          <header class="editor-header">
            <div>
              <p>Acquisition</p>
              <h2 id="transaction-editor-title">Gérer l’achat</h2>
              <span>{{ offer.title || "Achat sans titre" }}</span>
            </div>
            <button type="button" aria-label="Fermer" @click="close">×</button>
          </header>
          <form class="editor-form" @submit.prevent="save">
            <div class="editor-body">
              <section v-if="isManualOffer" class="editor-section">
                <div class="section-title">
                  <span>01</span>
                  <div>
                    <h3>Informations générales</h3>
                    <p>Identifiez l’achat et conservez son lien d’origine.</p>
                  </div>
                </div>
                <div class="fields two-columns">
                  <label class="wide"
                    >Titre<input
                      v-model.trim="form.title"
                      type="text"
                      required /></label
                  ><label
                    >Référence interne<input
                      v-model.trim="form.reference"
                      type="text"
                      placeholder="Facultatif" /></label
                  ><label
                    >Identifiant du colis<input
                      v-model.trim="form.packageRef"
                      type="text"
                      placeholder="Facultatif" /></label
                  ><label class="wide"
                    >URL de l’annonce<input
                      v-model.trim="form.url"
                      type="url"
                      placeholder="https://…" /></label
                  ><label class="wide"
                    >Notes<textarea
                      v-model.trim="form.description"
                      rows="3"
                      placeholder="Informations complémentaires…"
                    ></textarea>
                  </label>
                </div>
              </section>
              <section class="editor-section">
                <div class="section-title">
                  <span>{{ isManualOffer ? "02" : "01" }}</span>
                  <div>
                    <h3>Coûts de l’acquisition</h3>
                    <p>
                      Détaillez les montants pour obtenir un coût total fiable.
                    </p>
                  </div>
                </div>
                <div class="fields cost-grid">
                  <label
                    >Prix d’achat
                    <div class="money-input">
                      <input
                        v-model.number="form.price"
                        type="number"
                        min="0"
                        step="0.01"
                        required
                      /><span>€</span>
                    </div></label
                  >
                  <label
                    >Commission
                    <div class="money-input">
                      <input
                        v-model.number="form.commission"
                        type="number"
                        min="0"
                        step="0.01"
                      /><span>€</span>
                    </div></label
                  >
                  <label
                    >TVA
                    <div class="money-input">
                      <input
                        v-model.number="form.vat"
                        type="number"
                        min="0"
                        step="0.01"
                      /><span>€</span>
                    </div></label
                  >
                  <label
                    >Livraison locale
                    <div class="money-input">
                      <input
                        v-model.number="form.localShipping"
                        type="number"
                        min="0"
                        step="0.01"
                      /><span>€</span>
                    </div></label
                  >
                  <label
                    >Emballage
                    <div class="money-input">
                      <input
                        v-model.number="form.package"
                        type="number"
                        min="0"
                        step="0.01"
                      /><span>€</span>
                    </div></label
                  >
                  <label
                    >Transport international
                    <div class="money-input">
                      <input
                        v-model.number="form.shipping"
                        type="number"
                        min="0"
                        step="0.01"
                      /><span>€</span>
                    </div></label
                  >
                  <label
                    >Douane et import
                    <div class="money-input">
                      <input
                        v-model.number="form.taxes"
                        type="number"
                        min="0"
                        step="0.01"
                      /><span>€</span>
                    </div></label
                  >
                </div>
              </section>
              <section
                v-if="!isManualOffer"
                class="editor-section compact-section"
              >
                <div class="section-title">
                  <span>02</span>
                  <div>
                    <h3>Références</h3>
                    <p>Reliez cet achat à vos références logistiques.</p>
                  </div>
                </div>
                <div class="fields two-columns">
                  <label
                    >Référence interne<input
                      v-model.trim="form.reference"
                      type="text"
                      placeholder="Facultatif" /></label
                  ><label
                    >Identifiant du colis<input
                      v-model.trim="form.packageRef"
                      type="text"
                      placeholder="Facultatif"
                  /></label>
                </div>
              </section>
            </div>
            <aside class="total-bar">
              <div>
                <span>Coût total calculé</span
                ><strong>{{ formattedTotal }}</strong>
              </div>
              <p>Prix d’achat et frais additionnels inclus.</p>
            </aside>
            <p v-if="error" class="save-error" role="alert">{{ error }}</p>
            <footer class="editor-footer">
              <button type="button" class="secondary" @click="close">
                Annuler</button
              ><button
                type="submit"
                class="primary"
                :disabled="!isValid || saving"
              >
                {{ saving ? "Enregistrement…" : "Enregistrer l’achat" }}
              </button>
            </footer>
          </form>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { computed, onMounted, onUnmounted, reactive, watch } from "vue";
export default {
  name: "OfferEditPopup",
  props: {
    visible: { type: Boolean, default: false },
    offer: { type: Object, required: true },
    // Saving in progress and its error, handled by the page that saves
    busy: { type: Boolean, default: false },
    error: { type: String, default: "" },
  },
  emits: ["close", "save"],
  setup(props, { emit }) {
    const saving = computed(() => props.busy);
    const form = reactive({
      price: 0,
      commission: 0,
      vat: 0,
      localShipping: 0,
      package: 0,
      shipping: 0,
      taxes: 0,
      source: "",
      title: "",
      url: "",
      description: "",
      reference: "",
      packageRef: "",
    });
    const sync = () => {
      const costs = props.offer.transactionCosts || {};
      Object.assign(form, {
        // The listing price is in yen for Yahoo and Mercari: not a price in euros
        price: Number(
          costs.price ??
            (props.offer.currency && props.offer.currency !== "EUR"
              ? 0
              : props.offer.price) ??
            0,
        ),
        commission: Number(costs.commission) || 0,
        vat: Number(costs.vat) || 0,
        localShipping: Number(costs.localShipping) || 0,
        package: Number(costs.package) || 0,
        shipping: Number(costs.shipping) || 0,
        taxes: Number(costs.taxes) || 0,
        source: props.offer.source || "",
        title: props.offer.title || "",
        url: props.offer.url || "",
        description: props.offer.description || "",
        reference: props.offer.reference || "",
        packageRef: props.offer.packageRef || "",
      });
    };
    const isManualOffer = computed(() => form.source === "manual");
    const total = computed(() =>
      [
        "price",
        "commission",
        "vat",
        "localShipping",
        "package",
        "shipping",
        "taxes",
      ].reduce((sum, key) => sum + (Number(form[key]) || 0), 0),
    );
    const formattedTotal = computed(() =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(total.value),
    );
    const isValid = computed(() =>
      isManualOffer.value ? Boolean(form.title) : Number(form.price) > 0,
    );
    const close = () => {
      if (!saving.value) emit("close");
    };
    const save = () => {
      if (!isValid.value || saving.value) return;
      emit("save", { ...form });
    };
    const escape = (event) => {
      if (event.key === "Escape" && props.visible) close();
    };
    watch(
      () => [props.visible, props.offer._key],
      () => {
        if (props.visible) sync();
      },
      { immediate: true },
    );
    onMounted(() => document.addEventListener("keydown", escape));
    onUnmounted(() => document.removeEventListener("keydown", escape));
    return {
      close,
      form,
      formattedTotal,
      isManualOffer,
      isValid,
      save,
      saving,
    };
  },
};
</script>

<style scoped>
.save-error {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fdecea;
  color: #b42318;
  font-size: 0.9rem;
}
.transaction-editor-layer {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: grid;
  place-items: center;
  padding: calc(24px + var(--safe-top)) 24px calc(24px + var(--safe-bottom));
  background: rgba(15, 23, 36, 0.48);
  backdrop-filter: blur(3px);
}
.transaction-editor {
  display: flex;
  width: min(880px, 100%);
  max-height: calc(100vh - 48px - var(--safe-top) - var(--safe-bottom));
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #dfe5ec;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 26px 80px rgba(15, 23, 36, 0.24);
}
.editor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 28px;
  border-bottom: 1px solid #e5eaf0;
}
.editor-header p {
  margin: 0 0 5px;
  color: #168a64;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.editor-header h2 {
  margin: 0;
  font-size: 21px;
}
.editor-header span {
  display: block;
  max-width: 680px;
  margin-top: 6px;
  overflow: hidden;
  color: #717d8e;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.editor-header button {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid #d7dee7;
  border-radius: 8px;
  background: #fff;
  color: #657083;
  font-size: 22px;
}
.editor-form {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  margin: 0;
  padding: 0;
  background: #fff;
  box-shadow: none;
}
.editor-body {
  overflow: auto;
}
.editor-section {
  padding: 26px 28px;
  border-bottom: 1px solid #e8ecf1;
}
.section-title {
  display: flex;
  gap: 14px;
  margin-bottom: 22px;
}
.section-title > span {
  display: grid;
  width: 32px;
  height: 25px;
  flex: 0 0 32px;
  place-items: center;
  border-radius: 6px;
  background: #e8f6f1;
  color: #168a64;
  font-size: 10px;
  font-weight: 800;
}
.section-title h3 {
  margin: 0 0 4px;
  font-size: 15px;
}
.section-title p {
  margin: 0;
  color: #788393;
  font-size: 12px;
}
.fields {
  margin-left: 46px;
}
.two-columns,
.cost-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.cost-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.fields label {
  display: flex;
  flex-direction: column;
  gap: 7px;
  color: #465164;
  font-size: 12px;
  font-weight: 700;
}
.fields .wide {
  grid-column: 1/-1;
}
.fields input,
.fields textarea {
  width: 100%;
  min-height: 40px;
  padding: 9px 11px;
  border: 1px solid #d3dae3;
  border-radius: 8px;
  background: #fff;
  color: #273244;
  font-size: 13px;
  font-weight: 400;
  outline: 0;
}
.fields textarea {
  resize: vertical;
}
.fields input:focus,
.fields textarea:focus {
  border-color: #5ba98f;
  box-shadow: 0 0 0 3px rgba(22, 138, 100, 0.09);
}
.money-input {
  position: relative;
}
.money-input input {
  padding-right: 32px;
}
.money-input span {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  color: #85909e;
  font-weight: 600;
}
.total-bar {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 17px 28px;
  border-bottom: 1px solid #e5eaf0;
  background: #f7faf9;
}
.total-bar div {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.total-bar span {
  color: #647082;
  font-size: 12px;
}
.total-bar strong {
  font-size: 20px;
}
.total-bar p {
  margin: 0 0 0 auto;
  color: #87919f;
  font-size: 11px;
}
.editor-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 18px 28px;
}
.editor-footer button {
  height: 40px;
  padding: 0 17px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
}
.editor-footer .secondary {
  border: 1px solid #cfd6df;
  background: #fff;
  color: #4e596a;
}
.editor-footer .primary {
  border: 1px solid #172235;
  background: #172235;
  color: #fff;
}
.editor-footer .primary:disabled {
  opacity: 0.4;
}
.transaction-editor-enter-active,
.transaction-editor-leave-active {
  transition: opacity 0.18s;
}
.transaction-editor-enter-from,
.transaction-editor-leave-to {
  opacity: 0;
}
@media (max-width: 700px) {
  .transaction-editor-layer {
    align-items: end;
    padding: 0;
  }
  .transaction-editor {
    max-height: 94vh;
    border-radius: 14px 14px 0 0;
  }
  .cost-grid,
  .two-columns {
    grid-template-columns: 1fr;
  }
  .fields {
    margin-left: 0;
  }
  .editor-header,
  .editor-section {
    padding: 22px 20px;
  }
  .total-bar {
    align-items: flex-start;
    flex-direction: column;
    padding: 15px 20px;
  }
  .total-bar p {
    margin: 0;
  }
  .editor-footer {
    padding: 16px 20px;
  }
  .editor-footer button {
    flex: 1;
  }
}
</style>
